import { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'

import { FormRegimeProduto } from '../../../components/form/shared/formRegimeProduto'

import { useCreateAgreement } from '../services/useCreateAgreement'
import { useFetchContractstById } from '../services/useFetchContractstById'
import { TypeContrato } from '../utils/enum'
import { useToast } from '@/lib/hooks/use-toast'

type AddProductProps = {
  id_client: string
}

export default function AddProduct({ id_client }: AddProductProps) {
  const [regimeTributario, setRegimeTributario] = useState<string | null>(null)
  const [produtosSelecionados, setProdutosSelecionados] = useState<string[]>([])
  const [produtosFixos, setProdutosFixos] = useState<string[]>([])
  const [modo, setModo] = useState<'editar' | 'adicionar'>('editar')
  const { data: agreementData, error, isLoading } = useFetchContractstById(id_client, { enabled: !!id_client })
  const { mutate: createAgreement } = useCreateAgreement(id_client)
  const { toast } = useToast()

  const regimesAtivos = useMemo(() => {
    if (!agreementData) return []

    return agreementData.map((a: { typeRegime: any }) => a.typeRegime).filter(Boolean)
  }, [agreementData])
  const regimesDisponiveis = useMemo(() => {
    const todos = [TypeContrato.LUCRO_REAL, TypeContrato.LUCRO_PRESUMIDO, TypeContrato.SIMPLES_NACIONAL]
    return todos.filter(r => !regimesAtivos.includes(r))
  }, [regimesAtivos])

  console.log(agreementData)
  useEffect(() => {
    if (!agreementData?.contracts) return

    if (modo === 'editar') {
      inicializarEdicao(agreementData)
    } else {
      limparFormulario()
    }
  }, [agreementData, modo])

  const inicializarEdicao = (data: any[]) => {
    if (!regimeTributario) return

    const regimeSelecionado = data.find(d => d.typeRegime === regimeTributario)

    if (!regimeSelecionado) return

    const regimes = [TypeContrato.LUCRO_REAL, TypeContrato.LUCRO_PRESUMIDO, TypeContrato.SIMPLES_NACIONAL]
    const produtos = regimeSelecionado.contracts.filter(
      (c: TypeContrato) => !regimes.includes(c) && c !== TypeContrato.PRINCIPAL
    )

    setProdutosSelecionados(produtos)
    setProdutosFixos(produtos)
  }

  const limparFormulario = () => {
    setRegimeTributario(null)
    setProdutosSelecionados([])
    setProdutosFixos([])
  }

  const handleRegimeChange = (value: string) => {
    setRegimeTributario(value)
    // resto do seu código atual...
    if (modo === 'editar') {
      const regimeSelecionado = agreementData?.find((d: { typeRegime: string }) => d.typeRegime === value)
      if (regimeSelecionado) {
        const produtosSalvos = regimeSelecionado.contracts.filter(
          (c: string) =>
            ![
              TypeContrato.LUCRO_REAL,
              TypeContrato.LUCRO_PRESUMIDO,
              TypeContrato.SIMPLES_NACIONAL,
              TypeContrato.PRINCIPAL
            ].includes(c as TypeContrato)
        )
        setProdutosSelecionados(produtosSalvos)
        setProdutosFixos(produtosSalvos)
      } else {
        setProdutosSelecionados([])
        setProdutosFixos([])
      }
    } else {
      setProdutosSelecionados([])
      setProdutosFixos([])
    }
  }

  const handleSubmit = () => {
    if (!regimeTributario)
      return toast({
        title: 'Regime tributário não selecionado.',
        description: 'Selecione um regime.'
      })
    const payload = {
      typeRegime: regimeTributario,
      contracts: [...produtosSelecionados, 'PRINCIPAL'] as TypeContrato[]
    }

    createAgreement(payload, {
      onSuccess: () => {
        setProdutosFixos(produtosSelecionados)
        toast({
          variant: 'success',
          title: 'Atualizado com sucesso!',
          description: `Produto adicionado e Regime atualizado com sucesso.`
        })
      },
      onError: () => {
        toast({
          variant: 'destructive',
          title: 'Não foi possível atualizar',
          description: 'Tente novamente em instantes.'
        })
      }
    })
  }

  if (isLoading) return <Loading />
  if (error) return <Error message={error.message} />

  return (
    <div className="w-full flex flex-col items-center">
      <ModoSelector modo={modo} setModo={setModo} canAdd={regimesDisponiveis.length > 0} />

      <FormRegimeProduto
        modo={modo}
        regimeTributario={regimeTributario}
        produtosSelecionados={produtosSelecionados}
        produtosFixos={produtosFixos}
        regimesDisponiveis={regimesDisponiveis}
        regimesAtivos={regimesAtivos}
        onRegimeChange={handleRegimeChange}
        onProdutoChange={produto => {
          if (produtosFixos.includes(produto)) return
          // resposta imediata ao clique (UI otimista local)
          setProdutosSelecionados(prev =>
            prev.includes(produto) ? prev.filter(p => p !== produto) : [...prev, produto]
          )
        }}
        onSubmit={handleSubmit}
      />
    </div>
  )
}

import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'

function ModoSelector({
  modo,
  setModo,
  canAdd
}: {
  modo: 'editar' | 'adicionar'
  setModo: (modo: 'editar' | 'adicionar') => void
  canAdd: boolean
}) {
  return (
    <div className="flex flex-col items-center gap-2">
      <Tabs
        value={modo}
        onValueChange={v => {
          // impede trocar para "adicionar" se não houver regimes disponíveis
          if (v === 'adicionar' && !canAdd) return
          setModo(v as 'editar' | 'adicionar')
        }}
      >
        <TabsList className="grid grid-cols-2">
          <TabsTrigger value="editar">Regime Atual</TabsTrigger>
          <TabsTrigger
            value="adicionar"
            disabled={!canAdd}
            className={!canAdd ? 'cursor-not-allowed opacity-60' : ''}
            title={!canAdd ? 'Todos os regimes já foram adicionados' : undefined}
          >
            Novo Regime
          </TabsTrigger>
        </TabsList>

        {/* opcional: conteúdos se quiser renderizar algo específico por aba */}
        <TabsContent value="editar" />
        <TabsContent value="adicionar" />
      </Tabs>
    </div>
  )
}

function Loading() {
  return (
    <div className="flex items-center justify-center py-10 text-foreground">
      <Loader2 className="w-6 h-6 animate-spin text-primary" />
    </div>
  )
}

function Error({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center py-10 text-red-500 font-semibold">Erro ao carregar: {message}</div>
  )
}
